import { useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from '../axios';
import {
	CompanyProfileAttachDocPayload,
	CompanyProfilePayload,
	CompanyProfileResponse,
} from '../../type/CompanyProfile';

// Get Company Profile By ID
const getCompanyProfileById = async (
	payload: CompanyProfilePayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/admin/getCompanyProfileById',
		payload,
	);
	return response.data.data;
};

export const useGetCompanyProfileById = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfilePayload>({
		mutationKey: ['getCompanyProfileById'],
		mutationFn: getCompanyProfileById,
	});
};

export const useQGetCompanyProfileById = (payload: any | null) => {
	return useQuery<any, Error, any>({
		queryKey: ['getCompanyProfileById', payload],
		queryFn: () => getCompanyProfileById(payload),
		enabled: payload.enable, // ✅ only run when _id is defined and truthy
	});
};

// Update Company Profile By ID
const updateCompanyProfileById = async (
	payload: CompanyProfilePayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/admin/updateCompanyProfileById',
		payload,
	);
	return response.data;
};

export const useUpdateCompanyProfileById = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfilePayload>({
		mutationKey: ['updateCompanyProfileById'],
		mutationFn: updateCompanyProfileById,
	});
};

// Upload Company profile Documents
const uploadCompanyProfileDoc = async (formData: FormData): Promise<CompanyProfileResponse> => {
	const response = await axiosInstance.post<CompanyProfileResponse>('/util/uploadDoc', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return response.data;
};

export const useUploadCompanyProfileDoc = () => {
	return useMutation<CompanyProfileResponse, Error, FormData>({
		mutationKey: ['uploadDoc'],
		mutationFn: uploadCompanyProfileDoc,
	});
};

// Attach Uploaded Doc
const addCompanyProfileDoc = async (
	payload: CompanyProfileAttachDocPayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/util/attachDoc',
		payload,
	);
	return response.data;
};

export const useAddCompanyProfileDoc = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfileAttachDocPayload>({
		mutationKey: ['attachDoc'],
		mutationFn: addCompanyProfileDoc,
	});
};

// Delete Uploaded Doc
const addDeleteCompanyProfileDoc = async (
	payload: CompanyProfileAttachDocPayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/util/deleteDoc',
		payload,
	);
	return response.data;
};

export const useDeleteCompanyProfileDoc = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfileAttachDocPayload>({
		mutationKey: ['deleteDoc'],
		mutationFn: addDeleteCompanyProfileDoc,
	});
};

// Remove Uploaded Doc
const removeDoc = async (payload: CompanyProfileAttachDocPayload): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>('/util/removeDoc', payload);
	return response.data;
};

export const useRemoveDoc = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfileAttachDocPayload>({
		mutationKey: ['removeDoc'],
		mutationFn: removeDoc,
	});
};

// Get PO Close Threshold
const getPOCloseThreshold = async (
	payload: CompanyProfilePayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/po/getPOCloseThreshold',
		payload,
	);
	return response.data.data;
};

export const useGetPOCloseThreshold = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfilePayload>({
		mutationKey: ['getPOCloseThreshold'],
		mutationFn: getPOCloseThreshold,
	});
};

// Update PO Close Threshold
const updatePOCloseThreshold = async (
	payload: CompanyProfilePayload,
): Promise<CompanyProfileResponse> => {
	const response: any = await axiosInstance.post<CompanyProfileResponse>(
		'/po/updatePOCloseThreshold',
		payload,
	);
	return response.data;
};

export const useUpdatePOCloseThreshold = () => {
	return useMutation<CompanyProfileResponse, Error, CompanyProfilePayload>({
		mutationKey: ['updatePOCloseThreshold'],
		mutationFn: updatePOCloseThreshold,
	});
};